import {Direction} from '@angular/cdk/bidi';
import {ScrollStrategy} from '@angular/cdk/overlay';
import {Directive, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef} from '@angular/core';
import {AutoFocusTarget, DialogPosition, DialogRole, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { DeviceService } from 'src/app/Data/Services/device/device-service.service';
import { DcColorType } from 'src/styles/doc-theme.data';
import { DialogControlService } from './dialog-control.service';

export const DcDialogEventType = {
	beforeClosed: 'beforeClosed',
	onOpened: 'onOpened',
	afterOpened: 'afterOpened',
	afterClosed: 'afterClosed',
	keydownEvents: 'keydownEvents',
	backdropClick: 'backdropClick',
} as const;
export type DcDialogEventType = keyof typeof DcDialogEventType;

export type DialogData = {
	openData?: any;
	closeData?: any;
};

export class DcDialogConfig<D = any> extends MatDialogConfig<D> {
	events: DcDialogEventType[] = [];

	/**
	 * Data to be injected into the dialog content component.
	 * @example:
	 * <ng-template dcDialog="id"
	 * let-data.prop1
	 * let-data.prop2
	 * let-data.prop3
	 * >
	 */
	override data?: D | null;

	borderColor: DcColorType = DcColorType.Primary;
	containerClass: string | string[] = [];
	zIndex = 1000;

	constructor(init?: Partial<DcDialogConfig>) {
		super();
		Object.assign(this, init);
	}
}

export interface DcDialogRefStructure {
	dialogDirective: DialogDirective;
	contextElement: TemplateRef<any>;
	dialogRef?: MatDialogRef<any>;
	config?: DcDialogConfig;
	subscriptions?: Subscription[];

	info: {
		isOpen: boolean;
	};
}

/**
 * @example:
 * <ng-template DcDialog="dialogId"
 *    height="auto"
 *    width="50%"
 *    maxheight="95vh"
 *    [hasbackdrop]="true"
 *    [mobileDialogDims]="['100%', 'auto', '98vw', '75vh']"
 *
 *    // to use data in template: {user:User, name:string}
 *    let-user="user"
 *    let-name="name
 * >
 * //// dialog content
 * </ng-template>
 */

@Directive({
	selector: '[DcDialog]',
	exportAs: 'DcDialog',
	standalone: true,
})
export class DialogDirective implements OnInit, OnDestroy {
	dialogRef: DcDialogRefStructure;

	@Input() DcDialog: string;

	@Input() dialogConfig: DcDialogConfig = undefined;

	//dialog properties
	@Input() role?: DialogRole;
	@Input() panelclass?: string | string[];
	@Input() hasbackdrop?: boolean;
	@Input()
	backdropclass?: string[];
	@Input() disableclose?: boolean;
	@Input() width?: string;
	@Input() height?: string;
	@Input() minwidth?: number | string;
	@Input() minheight?: number | string;
	@Input() maxwidth?: number | string;
	@Input() maxheight?: number | string;
	@Input() position?: DialogPosition;
	@Input() direction?: Direction;
	@Input() autofocus?: AutoFocusTarget | string;
	@Input() scrollstrategy?: ScrollStrategy;
	@Input() closeonnavigation?: boolean;
	@Input() borderColor: DcColorType = DcColorType.Primary;
	@Input() containerClass: string | string[] = [];
	@Input() zIndex = 1000;

	@Input() mdcDialogSurfaceClass = '';

	//custom dialog inputs and outputs
	/**
	 * @property mobileProps:[width: string | 'full', height: string | 'full']
	 */
	mobileProps:
		| {
				width: string;
				height: string;
				minWidth: string;
				minHeight: string;
				maxWidth: string;
				maxHeight: string;
		  }
		| undefined = undefined;

	/**
     [width, height, maxWidth, maxHeight, minWidth, minHeight]
     * */
	@Input() set mobileDialogDims(
		props: [
			width: string | 'full' | undefined,
			height: string | 'full' | undefined,
			maxWidth?: string | 'full' | undefined,
			maxHeight?: string | 'full' | undefined,
			minWidth?: string | 'full' | undefined,
			minHeight?: string | 'full' | undefined,
		],
	) {
		if (this.deviceService.isMobile || this.deviceService.isTablet) {
			const [width, height, maxWidth, maxHeight, minWidth, minHeight] = props;
			this.mobileProps = {
				width: '',
				height: '',
				minWidth: '',
				minHeight: '',
				maxWidth: '',
				maxHeight: '',
			};

			if (width == 'full') {
				this.fullWidth = true;
				this.mobileProps.width = this.mobileProps.maxWidth = this.mobileProps.minWidth = '98vw';
			} else if (typeof width == 'string') {
				this.mobileProps.width = this.width = width;
			}
			if (height == 'full') {
				this.fullHeight = true;
				this.mobileProps.height = this.mobileProps.maxHeight = this.mobileProps.minHeight = '98vh';
			} else if (typeof height == 'string') {
				this.height = this.mobileProps.height = height;
			}
			if (maxWidth == 'full') {
				this.maxwidth = '100%';
				this.mobileProps.maxWidth = '98vw';
			} else if (typeof maxWidth == 'string') {
				this.maxwidth = maxWidth;
				this.mobileProps.maxWidth = maxWidth;
			} else if (maxHeight == 'full') {
				this.mobileProps.maxHeight = this.maxheight = '100%';
			} else if (typeof maxHeight == 'string') {
				this.mobileProps.maxHeight = this.maxheight = maxHeight;
			}

			if (minWidth == 'full') {
				this.minwidth = '100%';
				this.mobileProps.minWidth = '98vw';
			} else if (typeof minWidth == 'string') {
				this.minwidth = minWidth;
				this.mobileProps.minWidth = minWidth;
			} else if (minHeight == 'full') {
				this.mobileProps.minHeight = this.minheight = '100%';
			} else if (typeof minHeight == 'string') {
				this.mobileProps.minHeight = this.minheight = minHeight;
			}
			this.fullScreen = [undefined, undefined];
		}
	}

	/** A setter for the fullScreen property:[width: boolean, height: boolean, onlyMobile: boolean]. */
	@Input() set fullScreen(props: [width: boolean, height: boolean] | true) {
		if (props == true) {
			this.fullWidth = true;
			this.fullHeight = true;
		} else {
			const [width, height] = props;
			if (width == undefined && height == undefined) return;
			if (width == true) this.fullWidth = true;
			if (height == true) this.fullHeight = true;
		}
	}

	@Input() set fullWidth(fullW: boolean) {
		if (fullW == true) {
			this.width = this.minwidth = this.maxwidth = '98vw';
		}
	}

	@Input() set fullHeight(fullH: boolean) {
		if (fullH == true) {
			this.height = '100%';
			this.minheight = this.maxheight = '98vh';
		}
	}

	@Output() OnOpened: EventEmitter<void> = this.dcDialogService.onOpened;
	@Output() OnClosed: EventEmitter<void> = this.dcDialogService.onClosed;

	@Output() dialogEvents: EventEmitter<[DcDialogEventType, any]> = new EventEmitter();

	@Output() dialogClientDimensions: EventEmitter<{width: number; height: number}> = new EventEmitter();

	constructor(
		public dialogHost: TemplateRef<any>,
		private dcDialogService: DialogControlService,
		private deviceService: DeviceService,
	) {}

	ngOnInit(): void {
		this.dialogRef = this.dcDialogService.registerDialog(this, this.DcDialog, this.dialogHost, this.generateDialogConfigurations());
		this.dcDialogService.dialogEvents.subscribe(([event, data]) => {
			this.dialogEvents.emit([event, data]);
		});
	}

	ngOnDestroy(): void {
		this.dcDialogService.unregisterDialog(this.DcDialog);
	}

	generateDialogConfigurations(): DcDialogConfig {
		if (this.dialogConfig == undefined) {
			const config = {
				id: this.DcDialog,
				role: this.role,
				panelClass: this.panelclass,
				hasBackdrop: this.hasbackdrop,
				backdropClass: this.backdropclass,
				disableClose: this.disableclose,
				direction: this.direction ? this.direction : 'rtl',
				ariaDescribedBy: '',
				ariaLabel: '',
				ariaLabelledBy: '',
				componentFactoryResolver: undefined,
				data: undefined,
				delayFocusTrap: false,
				enterAnimationDuration: '',
				events: [],
				exitAnimationDuration: '',
				injector: undefined,
				restoreFocus: false,
				viewContainerRef: undefined,
				position: this.position,
				autoFocus: this.autofocus,
				scrollStrategy: this.scrollstrategy,
				closeOnNavigation: this.closeonnavigation,
				width: this.width,
				height: this.height,
				minWidth: this.minwidth,
				minHeight: this.minheight,
				maxWidth: this.maxwidth,
				maxHeight: this.maxheight,
				containerClass: this.containerClass,
				zIndex: this.zIndex,
			};
			if ((this.deviceService.isMobile || this.deviceService.isTablet) && this.mobileProps != undefined) {
				config.width = this.mobileProps.width;
				config.height = this.mobileProps.height;
				config.minWidth = this.mobileProps.minWidth;
				config.minHeight = this.mobileProps.minHeight;
				config.maxWidth = this.mobileProps.maxWidth;
				config.maxHeight = this.mobileProps.maxHeight;
			}
			this.dialogConfig = new DcDialogConfig(config);
			this.dialogConfig.borderColor = this.borderColor;
			this.dialogConfig.containerClass = this.containerClass;
			this.dialogConfig.zIndex = this.zIndex;
		}
		const zIndexClass = this.zIndex ? `z-[${this.zIndex}]` : 'z-[1000]';
		if (zIndexClass) {
			const panelClass = this.dialogConfig.panelClass;
			if (panelClass) {
				if (Array.isArray(panelClass)) {
					panelClass.push(zIndexClass);
				} else {
					this.dialogConfig.panelClass = [panelClass, zIndexClass];
				}
			} else {
				this.dialogConfig.panelClass = [zIndexClass];
			}
		}
		return this.dialogConfig;
	}

	public open(): void {
		this.dcDialogService.open(this.DcDialog);
	}

	public close(data: any = undefined): void {
		data ? this.dcDialogService.close(this.DcDialog, data) : this.dcDialogService.close(this.DcDialog);
	}


}
